/**
 * Session Warning Context
 *
 * Central orchestration for session timing, warning display, and session extension.
 * Shows a warning modal 5 minutes before session expiry with an "Extend Session"
 * button. Works for both magic link (JWT) and SSO (Express session) users.
 *
 * For magic link users: reads the JWT exp claim to determine exact expiry time.
 * For SSO users: estimates expiry based on last activity (Express rolling: true
 * resets the session on every HTTP response).
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { useSessionExpired, emitSessionExpired } from "./SessionExpiredContext";
import { client } from "../apolloClient";
import { isMagicLinkUser, clearAuthState } from "../util/authCleanup";
import { getApiBaseUrl } from "../util/downloadUtils";
import {
  SESSION_DURATION_MS,
  SESSION_WARNING_BEFORE_EXPIRY_MS,
} from "../util/sessionConstants";
import SessionWarningModal from "../components/SessionWarningModal";

// ─── Module-level event emitter (callable outside React) ───
// Allows apolloClient.ts to notify this context of successful requests,
// which resets the session timer for SSO users (their Express session
// refreshes on every HTTP response via rolling: true).
const SESSION_ACTIVITY_EVENT = "session-activity";

export function emitSessionActivity(): void {
  window.dispatchEvent(new CustomEvent(SESSION_ACTIVITY_EVENT));
}

// ─── JWT decode helper ───

/**
 * Decode the exp claim from a JWT without signature verification.
 * Only used client-side to read the session expiry time.
 * @returns expiry timestamp in milliseconds, or null if decode fails
 */
function decodeJwtExp(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    // Base64url decode the payload (second part)
    const payload = parts[1];
    if (!payload) return null;
    // Add padding if needed — atob() requires valid base64 with = padding
    const padded =
      payload.replace(/-/g, "+").replace(/_/g, "/") +
      "=".repeat((4 - (payload.length % 4)) % 4);
    const decoded = atob(padded);
    const parsed = JSON.parse(decoded) as { exp?: number };

    if (typeof parsed.exp !== "number") return null;

    // JWT exp is in seconds, convert to milliseconds
    return parsed.exp * 1000;
  } catch {
    return null;
  }
}

// ─── Context definition ───

interface SessionWarningContextType {
  /** Whether the warning modal is currently showing */
  showWarning: boolean;
  /** Millisecond timestamp when the session expires */
  sessionExpiresAt: number | null;
}

const SessionWarningContext = createContext<
  SessionWarningContextType | undefined
>(undefined);

interface SessionWarningProviderProps {
  children: ReactNode;
}

export const SessionWarningProvider: React.FC<SessionWarningProviderProps> = ({
  children,
}) => {
  const { isSessionExpired } = useSessionExpired();

  const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [isExtending, setIsExtending] = useState(false);
  const [extensionError, setExtensionError] = useState<string | null>(null);

  // Track whether the current user is a magic link user (determined once on mount)
  const isMagicLinkRef = useRef(false);
  // Timer ref for cleanup
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Initialize session expiry on mount ───
  useEffect(() => {
    const magicLink = isMagicLinkUser();
    isMagicLinkRef.current = magicLink;

    if (magicLink) {
      // Magic link users: decode JWT exp claim for exact expiry time
      const token =
        localStorage.getItem("magicLinkSessionToken") ||
        sessionStorage.getItem("magicLinkSessionToken");

      if (token) {
        const expMs = decodeJwtExp(token);
        if (expMs) {
          setSessionExpiresAt(expMs);
        } else {
          // Token exists but is malformed — this is an unexpected state.
          // Emit session expired rather than masking with a phantom timer.
          console.warn(
            "[SessionWarning] Magic link token is malformed, triggering session expired",
          );
          emitSessionExpired();
        }
      }
    } else {
      // SSO users: estimate expiry from now + session duration
      setSessionExpiresAt(Date.now() + SESSION_DURATION_MS);
    }
  }, []);

  // ─── Schedule warning timer when sessionExpiresAt changes ───
  useEffect(() => {
    if (!sessionExpiresAt || isSessionExpired) return;

    // Clear any existing timer
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }

    const warningTime =
      sessionExpiresAt - SESSION_WARNING_BEFORE_EXPIRY_MS - Date.now();

    if (warningTime <= 0) {
      // Already past the warning threshold — show immediately
      setShowWarning(true);
    } else {
      warningTimerRef.current = setTimeout(() => {
        setShowWarning(true);
      }, warningTime);
    }

    return () => {
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
    };
  }, [sessionExpiresAt, isSessionExpired]);

  // ─── Hide warning when session actually expires ───
  useEffect(() => {
    if (isSessionExpired) {
      setShowWarning(false);
    }
  }, [isSessionExpired]);

  // ─── Listen for activity events (SSO users only) ───
  useEffect(() => {
    const handler = (): void => {
      // Only reset timer for SSO users — magic link expiry is fixed until
      // explicitly extended via the endpoint
      if (!isMagicLinkRef.current) {
        setSessionExpiresAt(Date.now() + SESSION_DURATION_MS);
        // Dismiss warning if it was showing (activity means session was refreshed)
        setShowWarning(false);
      }
    };

    window.addEventListener(SESSION_ACTIVITY_EVENT, handler);
    return () => window.removeEventListener(SESSION_ACTIVITY_EVENT, handler);
  }, []);

  // ─── Extend session handler ───
  const extendSession = useCallback(async (): Promise<void> => {
    setIsExtending(true);
    setExtensionError(null);

    try {
      const baseUrl = getApiBaseUrl();
      const csrfToken = localStorage.getItem("csrfToken") || "";
      const response = await fetch(`${baseUrl}/extend-session`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "CSRF-Token": csrfToken,
        },
      });

      if (response.status === 401) {
        // Session fully expired beyond clock tolerance grace period — can't recover.
        // Transition to the SessionExpiredModal which has proper re-auth buttons.
        emitSessionExpired();
        return;
      }

      if (response.status === 403) {
        // CSRF token mismatch — try re-fetching the CSRF token and tell user to retry
        try {
          const csrfResponse = await fetch(`${baseUrl}/csrf-token`, {
            credentials: "include",
          });
          if (csrfResponse.ok) {
            const csrfData = await csrfResponse.json();
            if (csrfData.csrfToken) {
              localStorage.setItem("csrfToken", csrfData.csrfToken);
            }
          }
        } catch {
          // Ignore CSRF refresh failure — show the error message below
        }
        setExtensionError(
          "Security token expired. Please click Extend Session again.",
        );
        return;
      }

      if (!response.ok) {
        throw new Error(`Status ${response.status}`);
      }

      const data = await response.json();

      // Update expiry time from server response
      setSessionExpiresAt(new Date(data.expiresAt).getTime());
      setShowWarning(false);
      setExtensionError(null);

      // Store refreshed tokens (sessionToken is only present for magic link users)
      if (data.sessionToken) {
        localStorage.setItem("magicLinkSessionToken", data.sessionToken);
        sessionStorage.setItem("magicLinkSessionToken", data.sessionToken);
      }
      if (data.csrfToken) {
        localStorage.setItem("csrfToken", data.csrfToken);
      }
    } catch (error) {
      console.error("[SessionWarning] Failed to extend session:", error);
      setExtensionError(
        "Failed to extend session. Please try again or log out.",
      );
    } finally {
      setIsExtending(false);
    }
  }, []);

  // ─── Logout handler ───
  // POST to the backend /logout endpoint to destroy the server-side session and
  // clear auth cookies, then clear client-side state and redirect. Follows the
  // same pattern as App.tsx handleLogout.
  const handleLogout = useCallback(async (): Promise<void> => {
    try {
      const baseUrl = getApiBaseUrl();
      await fetch(`${baseUrl}/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      // Ignore errors — proceed with client cleanup regardless
      console.warn(
        "[SessionWarning] Logout request failed, continuing with client cleanup",
        e,
      );
    }
    await clearAuthState(client);
    window.location.href = "/logout-success";
  }, []);

  const value: SessionWarningContextType = {
    showWarning,
    sessionExpiresAt,
  };

  return (
    <SessionWarningContext.Provider value={value}>
      {children}
      {showWarning && !isSessionExpired && sessionExpiresAt && (
        <SessionWarningModal
          expiresAt={sessionExpiresAt}
          isExtending={isExtending}
          extensionError={extensionError}
          onExtend={extendSession}
          onLogout={handleLogout}
        />
      )}
    </SessionWarningContext.Provider>
  );
};

export const useSessionWarning = (): SessionWarningContextType => {
  const context = useContext(SessionWarningContext);
  if (context === undefined) {
    throw new Error(
      "useSessionWarning must be used within a SessionWarningProvider",
    );
  }
  return context;
};
